import React, { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import { useInView } from 'react-intersection-observer';
import { 
  Target, 
  Eye, 
  Heart, 
  Users, 
  Award, 
  BookOpen,
  CheckCircle,
  ArrowRight,
  Star,
  Clock,
  TrendingUp,
  Shield,
  Zap
} from 'lucide-react';
import { companyAPI, teamMembersAPI } from '../services/api';
import AnimatedCounter from '../components/common/AnimatedCounter';
import SEO from '../components/common/SEO'
import { Link } from 'react-router-dom';
const About = () => {
  const [company, setCompany] = useState(null);
  const [teamMembers, setTeamMembers] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchData = async () => {
      try {
        setLoading(true);
        const [companyResponse, teamResponse] = await Promise.all([
          companyAPI.get(),
          teamMembersAPI.getAll()
        ]);
        
        setCompany(companyResponse.data.data);
        setTeamMembers(teamResponse.data.data || []);
      } catch (error) {
        console.error('Error fetching data:', error);
      } finally {
        setLoading(false);
      }
    };

    fetchData();
  }, []);

  const [ref, inView] = useInView({
    threshold: 0.1,
    triggerOnce: true
  });

  let values = company?.values || [
    'Excellence in Education',
    'Innovation in Teaching',
    'Industry Relevance',
    'Student Success',
    'Continuous Improvement',
    'Ethical Practice'
  ];

    values =  JSON.parse(company?.values || JSON.stringify(values));
  // Use team members from API or fallback to empty array
  const team = teamMembers.length > 0 ? teamMembers.map(member => ({
    name: member.name,
    role: member.position,
    image: member.image,
    bio: member.bio,
    socialMedia: member.socialMedia,
    expertise: member.socialMedia?.expertise || []
  })) : [];

  // Use ourJourney from API without fallback
  let milestones = company?.ourJourney || [];
  milestones =  JSON.parse(company?.values || JSON.stringify(values));
  const containerVariants = {
    hidden: { opacity: 0 },
    visible: {
      opacity: 1,
      transition: {
        staggerChildren: 0.2,
        delayChildren: 0.3
      }
    }
  };

  const itemVariants = {
    hidden: { opacity: 0, y: 30 },
    visible: {
      opacity: 1,
      y: 0,
      transition: {
        duration: 0.8,
        ease: 'easeOut'
      }
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="loading-spinner"></div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-white dark:bg-gray-900">
        <SEO 
        title="About Us"
        description="Learn more about Prime Edge, our mission, vision, and the values that drive our engineering training programs."
        keywords="about prime edge, mission, vision, values, engineering training"
      />
      {/* Hero Section */}
      <section className="bg-gradient-to-br from-primary-600 to-accent-600 text-white py-20">
        <div className="container-custom">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            className="text-center max-w-4xl mx-auto"
          >
            <h1 className="text-4xl md:text-5xl lg:text-6xl font-bold mb-6">
              About Prime Edge
            </h1>
            <p className="text-xl md:text-2xl text-blue-100 leading-relaxed">
              {company?.description || 'Engineering Excellence Through Innovation. We provide world-class engineering training and professional development opportunities.'}
            </p>
          </motion.div>
        </div>
      </section>

      {/* Mission & Vision */}
      <section className="section-padding bg-gray-50 dark:bg-gray-800">
        <div className="container-custom">
          <motion.div
            ref={ref}
            variants={containerVariants}
            initial="hidden"
            animate={inView ? "visible" : "hidden"}
            className="grid grid-cols-1 lg:grid-cols-2 gap-16 items-center"
          >
            <motion.div variants={itemVariants}>
              <div className="space-y-8">
                <div className="p-8 bg-white dark:bg-gray-900 rounded-2xl shadow-lg">
                  <div className="flex items-start space-x-4">
                    <div className="w-16 h-16 bg-primary-600 rounded-2xl flex items-center justify-center flex-shrink-0">
                      <Target className="w-8 h-8 text-white" />
                    </div>
                    <div>
                      <h3 className="text-2xl font-bold text-gray-900 dark:text-white mb-4">Our Mission</h3>
                      <p className="text-gray-600 dark:text-gray-300 leading-relaxed">
                        {company?.mission || 'To empower engineers with cutting-edge knowledge, practical skills, and innovative thinking that drives technological advancement and professional excellence.'}
                      </p>
                    </div>
                  </div>
                </div>

                <div className="p-8 bg-white dark:bg-gray-900 rounded-2xl shadow-lg">
                  <div className="flex items-start space-x-4">
                    <div className="w-16 h-16 bg-accent-600 rounded-2xl flex items-center justify-center flex-shrink-0">
                      <Eye className="w-8 h-8 text-white" />
                    </div>
                    <div>
                      <h3 className="text-2xl font-bold text-gray-900 dark:text-white mb-4">Our Vision</h3>
                      <p className="text-gray-600 dark:text-gray-300 leading-relaxed">
                        {company?.vision || 'To be the global leader in engineering education, shaping the next generation of innovative engineers who will solve tomorrow\'s challenges.'}
                      </p>
                    </div>
                  </div>
                </div>
              </div>
            </motion.div>

            <motion.div variants={itemVariants}>
              <div className="space-y-6">
                <h2 className="text-3xl font-bold text-gray-900 dark:text-white mb-6">Our Values</h2>
                {values.map((value, index) => (
                  <motion.div
                    key={index}
                    variants={itemVariants}
                    className="flex items-center space-x-4 p-4 bg-white dark:bg-gray-900 rounded-xl shadow-lg"
                  >
                    <Heart className="w-6 h-6 text-primary-600 flex-shrink-0" />
                    <span className="text-lg text-gray-700 dark:text-gray-300">{value}</span>
                  </motion.div>
                ))}
              </div>
            </motion.div>
          </motion.div>
        </div>
      </section>

      {/* Stats Section */}
      <section className="section-padding bg-gradient-to-br from-primary-600 to-accent-600 text-white">
        <div className="container-custom">
          <motion.div
            variants={containerVariants}
            initial="hidden"
            animate={inView ? "visible" : "hidden"}
            className="text-center mb-16"
          >
            <motion.h2 
              variants={itemVariants}
              className="text-4xl md:text-5xl font-bold mb-6"
            >
              Our Impact in Numbers
            </motion.h2>
            <motion.p 
              variants={itemVariants}
              className="text-xl text-blue-100 max-w-3xl mx-auto"
            >
              We're proud of the impact we've made in the engineering community worldwide.
            </motion.p>
          </motion.div>

          <motion.div
            variants={containerVariants}
            initial="hidden"
            animate={inView ? "visible" : "hidden"}
            className="grid grid-cols-2 md:grid-cols-4 gap-8"
          >
            <motion.div variants={itemVariants} className="text-center">
              <div className="w-20 h-20 bg-white/20 rounded-2xl flex items-center justify-center mx-auto mb-4">
                <Users className="w-10 h-10 text-white" />
              </div>
              <AnimatedCounter
                end={company?.stats?.studentsTrained || 5000}
                suffix="+"
                className="text-4xl font-bold text-white mb-2"
              />
              <div className="text-blue-100">Students Trained</div>
            </motion.div>

            <motion.div variants={itemVariants} className="text-center">
              <div className="w-20 h-20 bg-white/20 rounded-2xl flex items-center justify-center mx-auto mb-4">
                <BookOpen className="w-10 h-10 text-white" />
              </div>
              <AnimatedCounter
                end={company?.stats?.coursesOffered || 50}
                suffix="+"
                className="text-4xl font-bold text-white mb-2"
              />
              <div className="text-blue-100">Courses Offered</div>
            </motion.div>

            <motion.div variants={itemVariants} className="text-center">
              <div className="w-20 h-20 bg-white/20 rounded-2xl flex items-center justify-center mx-auto mb-4">
                <Award className="w-10 h-10 text-white" />
              </div>
              <AnimatedCounter
                end={company?.stats?.successRate || 98}
                suffix="%"
                className="text-4xl font-bold text-white mb-2"
              />
              <div className="text-blue-100">Success Rate</div>
            </motion.div>

            <motion.div variants={itemVariants} className="text-center">
              <div className="w-20 h-20 bg-white/20 rounded-2xl flex items-center justify-center mx-auto mb-4">
                <Clock className="w-10 h-10 text-white" />
              </div>
              <AnimatedCounter
                end={company?.stats?.yearsExperience || 15}
                suffix="+"
                className="text-4xl font-bold text-white mb-2"
              />
              <div className="text-blue-100">Years Experience</div>
            </motion.div>
          </motion.div>
        </div>
      </section>

      {/* Team Section */}
      <section className="section-padding bg-white dark:bg-gray-900">
        <div className="container-custom">
          <motion.div
            variants={containerVariants}
            initial="hidden"
            animate={inView ? "visible" : "hidden"}
            className="text-center mb-16"
          >
            <motion.h2 
              variants={itemVariants}
              className="text-4xl md:text-5xl font-bold text-gray-900 dark:text-white mb-6"
            >
              Meet Our Team
            </motion.h2>
            <motion.p 
              variants={itemVariants}
              className="text-xl text-gray-600 dark:text-gray-300 max-w-3xl mx-auto"
            >
              Our team of experts is dedicated to providing you with the best learning experience possible.
            </motion.p>
          </motion.div>

          <motion.div
            variants={containerVariants}
            initial="hidden"
            animate={inView ? "visible" : "hidden"}
            className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8"
          >
            {team.length > 0 ? (
              team.map((member, index) => (
                <motion.div
                  key={index}
                  variants={itemVariants}
                  whileHover={{ y: -10 }}
                  className="bg-white dark:bg-gray-800 rounded-2xl shadow-lg p-6 text-center hover:shadow-xl transition-all duration-300"
                >
                  {member.image ? (
                    <img 
                      src={member.image} 
                      alt={member.name} 
                      className="w-24 h-24 object-cover rounded-full mx-auto mb-4"
                    />
                  ) : (
                    <div className="w-24 h-24 bg-gradient-to-br from-primary-100 to-accent-100 dark:from-primary-900 dark:to-accent-900 rounded-full flex items-center justify-center mx-auto mb-4">
                      <Users className="w-12 h-12 text-primary-600 dark:text-primary-400" />
                    </div>
                  )}
                  <h3 className="text-xl font-semibold text-gray-900 dark:text-white mb-2">
                    {member.name}
                  </h3>
                  <p className="text-primary-600 dark:text-primary-400 font-medium mb-3">
                    {member.role}
                  </p>
                  <p className="text-gray-600 dark:text-gray-300 text-sm mb-4">
                    {member.bio}
                  </p>
                  {member.expertise && member.expertise.length > 0 && (
                    <div className="space-y-1">
                      {member.expertise.map((skill, idx) => (
                        <span
                          key={idx}
                          className="inline-block px-3 py-1 bg-primary-100 dark:bg-primary-900 text-primary-800 dark:text-primary-200 text-xs rounded-full mr-1 mb-1"
                        >
                          {skill}
                        </span>
                      ))}
                    </div>
                  )}
                </motion.div>
              ))
            ) : (
              <div className="col-span-4 text-center py-8">
                <p className="text-gray-500 dark:text-gray-400">No team members found.</p>
              </div>
            )}
          </motion.div>
        </div>
      </section>

      {/* Timeline Section */}
      <section className="section-padding bg-gray-50 dark:bg-gray-800">
        <div className="container-custom">
          <motion.div
            variants={containerVariants}
            initial="hidden"
            animate={inView ? "visible" : "hidden"}
            className="text-center mb-16"
          >
            <motion.h2 
              variants={itemVariants}
              className="text-4xl md:text-5xl font-bold text-gray-900 dark:text-white mb-6"
            >
              Our Journey
            </motion.h2>
            <motion.p 
              variants={itemVariants}
              className="text-xl text-gray-600 dark:text-gray-300 max-w-3xl mx-auto"
            >
              From humble beginnings to becoming a global leader in engineering education.
            </motion.p>
          </motion.div>

          <motion.div
            variants={containerVariants}
            initial="hidden"
            animate={inView ? "visible" : "hidden"}
            className="relative"
          >
            {/* Timeline Line */}
            <div className="absolute left-1/2 transform -translate-x-1/2 w-1 h-full bg-primary-200 dark:bg-primary-800"></div>

            <div className="space-y-12">
              {milestones.map((milestone, index) => (
                <motion.div
                  key={index}
                  variants={itemVariants}
                  className={`flex items-center ${index % 2 === 0 ? 'flex-row' : 'flex-row-reverse'}`}
                >
                  <div className={`w-1/2 ${index % 2 === 0 ? 'pr-8 text-right' : 'pl-8 text-left'}`}>
                    <div className="bg-white dark:bg-gray-900 rounded-2xl shadow-lg p-6">
                      <div className="text-2xl font-bold text-primary-600 dark:text-primary-400 mb-2">
                        {milestone.year}
                      </div>
                      <h3 className="text-xl font-semibold text-gray-900 dark:text-white mb-2">
                        {milestone.title}
                      </h3>
                      <p className="text-gray-600 dark:text-gray-300">
                        {milestone.description}
                      </p>
                    </div>
                  </div>
                  
                  <div className="w-8 h-8 bg-primary-600 rounded-full flex items-center justify-center relative z-10">
                    <div className="w-4 h-4 bg-white rounded-full"></div>
                  </div>
                  
                  <div className="w-1/2"></div>
                </motion.div>
              ))}
            </div>
          </motion.div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="section-padding bg-gradient-to-br from-primary-600 to-accent-600 text-white">
        <div className="container-custom text-center">
          <motion.div
            variants={containerVariants}
            initial="hidden"
            animate={inView ? "visible" : "hidden"}
          >
            <motion.h2 
              variants={itemVariants}
              className="text-4xl md:text-5xl font-bold mb-6"
            >
              Ready to Join Our Community?
            </motion.h2>
            <motion.p 
              variants={itemVariants}
              className="text-xl text-blue-100 mb-8 max-w-3xl mx-auto"
            >
              Become part of our growing community of successful engineers and start your journey with Prime Edge today.
            </motion.p>
            <motion.div 
              variants={itemVariants}
              className="flex flex-col sm:flex-row gap-4 justify-center"
            >
              <Link to="/courses" className="btn btn-secondary btn-xl inline-flex items-center">
                Explore Courses
                <ArrowRight className="w-5 h-5 ml-2" />
              </Link>
              <Link to={"/contact"} className="btn btn-outline btn-xl inline-flex items-center border-2 border-white text-white hover:bg-white hover:text-primary-600">
                Contact Us
              </Link>
            </motion.div>
          </motion.div>
        </div>
      </section>
    </div>
  );
};

export default About;

